/**
 * Focused Search Plugin for Search Results Map
 *
 * Activates a viewport-scoped search when the map reaches zoom level 11,
 * but only when the pan/zoom that crossed the threshold was a real user
 * gesture (drag, scroll/pinch zoom, double-click zoom, or a zoom-control
 * click) — never as a side effect of a programmatic camera move (e.g.
 * another plugin's fitBounds/setMapZoom/updateMapCenter). See
 * mapGestureHandlers for how the gesture is detected. Deep-link
 * restoration (below) is the one deliberate exception. While active,
 * listing cards, result counts, and map pins all narrow to the visible
 * map area on every pan or idle.
 *
 * Deactivates when:
 *  - The user zooms below 11 (US3)
 *  - The location type transitions to rectangularArea (US4)
 *  - The result type transitions to builders (Builders tab)
 *
 * Also handles deep-link restoration: if the page loads with minlat/maxlat/
 * minlng/maxlng params, the map is fitted to those bounds and zoomed to the
 * focused-search threshold on the first idle so focused search activates normally.
 *
 * @module searchResultsMap/plugins/focusedSearch.plugin
 */

import {
  distinctUntilChanged,
  debounceTime,
  filter,
  map,
  pairwise,
  skip,
  take,
} from 'rxjs';
import {
  searchParametersActions,
  selectLocation,
  selectLocationType,
  selectResultType,
  selectFocusedSearchActive,
  LocationType,
  ResultType,
} from 'features/searchResultsFacets';
import { selectGeoCoordinates } from '../redux/selectors';
import { FOCUSED_SEARCH_ZOOM_THRESHOLD } from '../constants';
import { areBoundsValid } from '../services/geometry';
import createMapGestureHandlers from '../handlers/mapGestureHandlers';

const RECTANGULAR_AREA = LocationType.RectangularArea.toString();
const BUILDERS = ResultType.Builders.toString();

export default function createFocusedSearchPlugin() {
  const state = {
    started: false,
    subscriptions: [],
    mapFacade: null,
    store$: null,
    dispatch: null,
    getState: null,
    gestureHandlers: createMapGestureHandlers(),
    hasUserGesture: false,
  };

  function isFocusedSearchActive() {
    return selectFocusedSearchActive(state.getState());
  }

  function dispatchBounds(bounds) {
    state.dispatch(searchParametersActions.setFocusedSearchBounds({
      south: bounds.south,
      north: bounds.north,
      west: bounds.west,
      east: bounds.east,
    }));
  }

  function activate(bounds) {
    if (isFocusedSearchActive()) { return; }
    state.dispatch(searchParametersActions.setFocusedSearchActive(true));
    dispatchBounds(bounds);
  }

  function deactivate() {
    if (!isFocusedSearchActive()) { return; }
    state.dispatch(searchParametersActions.setFocusedSearchActive(false));
    state.dispatch(searchParametersActions.clearFocusedSearchBounds());
  }

  function subscribeToMapMoves() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectGeoCoordinates),
        filter(areBoundsValid),
        distinctUntilChanged(),
        debounceTime(300),
        filter(() => {
          const currentState = state.getState();
          const locationType = selectLocationType(currentState);
          const resultType = selectResultType(currentState);
          return locationType !== RECTANGULAR_AREA && resultType !== BUILDERS;
        }),
      ).subscribe(() => {
        const snapshot = state.mapFacade?.getCameraSnapshot?.();
        const zoom = snapshot?.zoom ?? 0;
        const bounds = snapshot?.bounds;

        if (zoom >= FOCUSED_SEARCH_ZOOM_THRESHOLD && bounds) {
          if (isFocusedSearchActive()) {
            dispatchBounds(bounds);
          } else if (state.hasUserGesture) {
            activate(bounds);
          }
        } else if (isFocusedSearchActive()) {
          deactivate();
        }

        // Each settled camera position requires its own fresh gesture — a drag from
        // several moves ago must not authorize a later, unrelated programmatic jump.
        state.hasUserGesture = false;
      }),
    );
  }

  function handleLocationTypeTransition([prev, curr]) {
    if (prev === RECTANGULAR_AREA) {
      // subscribeToMapMoves ignores every map move while locationType is
      // RectangularArea, so a gesture made while dragging in that mode never
      // reaches the "each settled position requires its own fresh gesture" reset
      // at the end of its subscribe callback. Left uncleared, that stale true
      // would silently authorize focused search to activate for whatever
      // location comes next (e.g. if the new location's default zoom already
      // sits at or above FOCUSED_SEARCH_ZOOM_THRESHOLD).
      state.hasUserGesture = false;
    }

    if (curr === RECTANGULAR_AREA && isFocusedSearchActive()) {
      // Intentionally do not dispatch clearFocusedSearchBounds() here:
      // fetchLocations for the rectangular-area search has already written
      // its own bounds into the shared lat/lng fields; clearing them now
      // would race with (and potentially null out) those rectangle bounds.
      state.dispatch(searchParametersActions.setFocusedSearchActive(false));
    }
  }

  function subscribeToLocationTypeChange() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocationType),
        distinctUntilChanged(),
        pairwise(),
      ).subscribe(handleLocationTypeTransition),
    );
  }

  // mapViewport.plugin re-centers the map (updateMapCenter) whenever the search origin
  // changes — a new location's viewport is not a continuation of whatever focused search
  // session was active for the previous one, so it must end here rather than carry over
  // via the dispatchBounds branch in subscribeToMapMoves (which isn't gated by
  // hasUserGesture — that gate only covers entering focused search, not staying in it).
  function subscribeToLocationChange() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocation),
        map((location) => ({
          originLatitude: Number(location?.originLatitude),
          originLongitude: Number(location?.originLongitude),
        })),
        filter(
          ({ originLatitude, originLongitude }) =>
            !Number.isNaN(originLatitude) && !Number.isNaN(originLongitude)
        ),
        distinctUntilChanged(
          (prev, curr) =>
            prev.originLatitude === curr.originLatitude &&
            prev.originLongitude === curr.originLongitude
        ),
        skip(1),
      ).subscribe(() => {
        state.hasUserGesture = false;
        deactivate();
      }),
    );
  }

  function subscribeToResultTypeChange() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectResultType),
        distinctUntilChanged(),
        pairwise(),
        filter(([, curr]) => curr === BUILDERS),
        filter(() => isFocusedSearchActive()),
      ).subscribe(deactivate),
    );
  }

  function subscribeToInitialFocusedSearch() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocation),
        take(1),
        filter(Boolean),
        map(({ minimumLatitude, maximumLatitude, minimumLongitude, maximumLongitude }) => ({
          minLatitude: parseFloat(minimumLatitude),
          maxLatitude: parseFloat(maximumLatitude),
          minLongitude: parseFloat(minimumLongitude),
          maxLongitude: parseFloat(maximumLongitude),
        })),
        filter(({ minLatitude, maxLatitude, minLongitude, maxLongitude }) =>
          [minLatitude, maxLatitude, minLongitude, maxLongitude].every(Number.isFinite)
        ),
        filter(() => {
          const locationType = selectLocationType(state.getState());
          return locationType !== RECTANGULAR_AREA;
        }),
      ).subscribe((bounds) => {
        state.mapFacade?.onceMapEvent?.('idle', () => {
          // Restoring a deep-link (minlat/maxlat/minlng/maxlng in the URL) represents a
          // choice the user already made — treat it as equivalent to a real gesture so
          // focused search still activates on the resulting idle despite being programmatic.
          state.hasUserGesture = true;
          state.mapFacade?.fitBounds?.(bounds);
          state.mapFacade?.setMapZoom?.(FOCUSED_SEARCH_ZOOM_THRESHOLD);
        });
      })
    );
  }

  function clearSubscriptions() {
    state.subscriptions.forEach((sub) => sub?.unsubscribe?.());
    state.subscriptions = [];
  }

  return {
    name: 'focused-search',

    async init(context) {
      context.state.focusedSearch = { started: false };
    },

    async start(context) {
      if (state.started) { return; }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.dispatch = context.services.dispatch;
      state.getState = context.services.getState;

      if (!state.mapFacade || !state.store$ || !state.dispatch || !state.getState) {
        return;
      }

      state.gestureHandlers.bind(state.mapFacade, context.root, {
        onUserGesture: () => { state.hasUserGesture = true; },
      });

      subscribeToMapMoves();
      subscribeToLocationChange();
      subscribeToLocationTypeChange();
      subscribeToResultTypeChange();
      subscribeToInitialFocusedSearch();

      state.started = true;
      context.state.focusedSearch = { started: true };
    },

    async stop(context) {
      clearSubscriptions();
      state.gestureHandlers.unbind();

      if (isFocusedSearchActive()) {
        state.dispatch(searchParametersActions.setFocusedSearchActive(false));
        state.dispatch(searchParametersActions.clearFocusedSearchBounds());
      }

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.dispatch = null;
      state.getState = null;
      state.hasUserGesture = false;

      delete context.state.focusedSearch;
    },
  };
}
